import { useCallback, useEffect, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import isEqual from "lodash/isEqual";
import { useDispatch, useSelector } from "react-redux";

import { getInitialMetricsStructure } from "../helpers/apiHelpers";
import { initMetricData } from "../store/actions/report";
import { getCurrentTabMetricsEffect } from "../store/effects/report";
import { selectIsBenchmarkFetched } from "../store/selectors/entitySelectors";
import { selectAllMetrics } from "../store/selectors/reportSelectors";

const useMetricsRequest = (tab: number) => {
    const {
        params: { key: benchmarkId, duration, country, granularity },
    } = useCurrentStateAndParams();

    const dispatch = useDispatch();
    const isBenchmarksFetched: boolean = useSelector(selectIsBenchmarkFetched);
    const metrics = useSelector(selectAllMetrics);

    const prevParams = useRef({});

    useEffect(() => {
        // No need to fetch anything for a new "channels" tab
        if (tab === 2) {
            return;
        }

        const newParams = { key: benchmarkId, duration, country, granularity };
        const isParamsChanged = !isEqual(prevParams.current, newParams);

        if (isParamsChanged || !metrics) {
            dispatch(initMetricData(getInitialMetricsStructure()));
        }

        if (isBenchmarksFetched) {
            dispatch(getCurrentTabMetricsEffect(benchmarkId, country, duration, granularity, tab));
        }

        if (isParamsChanged) {
            prevParams.current = newParams;
        }
    }, [isBenchmarksFetched, duration, country, granularity, tab, benchmarkId, dispatch]);

    const refetch = useCallback(() => {
        if (isBenchmarksFetched && tab !== 2) {
            dispatch(initMetricData(getInitialMetricsStructure()));
            void dispatch(getCurrentTabMetricsEffect(benchmarkId, country, duration, granularity, tab));
        }
    }, [isBenchmarksFetched, benchmarkId, country, duration, granularity, tab, dispatch]);

    return { refetch };
};

export default useMetricsRequest;
